import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Module from '@modules/projects/projectSetting/module';
import {
  columns as configColumns,
  projectSettingDefaultColumns as configDefaultColumns,
  generateGroupColumn,
} from '@modules/projects/projectsForm/customizeModule/manageMForm/config/columns.config';
// import '@style/modules/project/projectSettingModule.scss';
import '../../../styles/modules/project/projectSettingModule.scss';
import { eventNames } from '@utils/eventListener';
import { DEFALUT_PERMISSION_SECTION } from '@model';
import { Tooltip } from 'antd';
import { FORM_KEYS } from '@modules/projects/projectsForm/customizeModule/manageMForm/config/form.keys';

const GROUP_DEFAULT_CHECKED_MODULES = [
  DEFALUT_PERMISSION_SECTION.PEOJECT_LIST,
  DEFALUT_PERMISSION_SECTION.HOME,
];

const addStickStyle = (col, index, columns) => {
  if (index <= 7 && col.style) {
    const accumulateLeft =
      index === 0
        ? { number: 0, unit: '' }
        : columns.slice(0, index).reduce(
            (sum, current) => {
              if (!sum.unit && typeof current.style.width === 'string') {
                sum.unit = current.style.width.replace(/\d|\./g, '');
              }
              sum.number = sum.number + (parseFloat(current.style.width) || 0);
              return sum;
            },
            { number: 0, unit: '' },
          );
    const enhancedCol = {
      ...col,
      style: {
        ...col.style,
        position: 'sticky',
        left: accumulateLeft.number + accumulateLeft.unit,
        zIndex: 1,
        // borderRight: index === 6 ? '1px solid #474747' : undefined,
      },
    };
    if (enhancedCol.key === FORM_KEYS.ICON.key) {
      enhancedCol.formConfig = {
        ...enhancedCol.formConfig,
        toggleZIndex: true,
      };
    }
    return enhancedCol;
  }
  return col;
};

function ProjectModuleSetting(props) {
  const {
    getGroupData,
    moduleFormData,
    changeTab,
    cRef,
    renderAction,
    createStep = false,
  } = props;
  const [groupColumns, setGroupColumns] = useState([]);
  useEffect(() => {
    if ((moduleFormData || createStep) && typeof getGroupData === 'function') {
      const groupData = getGroupData();
      if (groupData) {
        setGroupColumns(
          groupData.map((i) =>
            generateGroupColumn(
              i.groupInfo.groupName,
              i.groupInfo.id,
              i.groupMembers.every((i) => i.isClient || i.type === 'Client'),
              i.groupMembers.length === 0,
            ),
          ),
        );
      }
    }
  }, [moduleFormData]);

  const columnProps = useMemo(() => {
    const columns = configColumns.map(addStickStyle);
    columns.splice(-1, 0, ...groupColumns);
    const defaultColumns = configDefaultColumns.map(addStickStyle);
    return {
      columns,
      defaultColumns: [...defaultColumns, ...groupColumns],
    };
  }, [groupColumns]);

  useEffect(() => {
    const { eventListener } = props;
    if (eventListener && !createStep) {
      eventListener.on(eventNames.groupChange, function (type, changedGroup) {
        if (type === 'add') {
          const form = cRef.current.getModuleForm();
          const formValues = form.getFieldsValue();
          const moduleRows = formValues?.customizeModule;
          if (
            Array.isArray(moduleRows) &&
            moduleRows.some((i) =>
              GROUP_DEFAULT_CHECKED_MODULES.includes(i.value),
            )
          ) {
            moduleRows.forEach((row) => {
              if (GROUP_DEFAULT_CHECKED_MODULES.includes(row.value)) {
                const newPermissionItem = {
                  groupId: changedGroup.id,
                  groupPermission: [row.value],
                };
                if (Array.isArray(row.groupPermissions)) {
                  row.groupPermissions.push(newPermissionItem);
                } else {
                  row.groupPermissions = [newPermissionItem];
                }
              }
            });
            form.setFieldsValue(formValues);
          }
          // set form value before update columns
          setGroupColumns((columns) => [
            ...columns,
            generateGroupColumn(
              changedGroup.groupName,
              changedGroup.id,
              true,
              true,
            ),
          ]);
        }
        if (type === 'delete') {
          const form = cRef.current.getModuleForm();
          const formValues = form.getFieldsValue();
          const moduleRows = formValues?.customizeModule;
          if (Array.isArray(moduleRows)) {
            moduleRows.forEach((row) => {
              if (Array.isArray(row.groupPermissions)) {
                row.groupPermissions = row.groupPermissions.filter(
                  (i) => i.groupId !== changedGroup.id,
                );
              }
            });
            form.setFieldsValue(formValues);
          }
          setGroupColumns((columns) =>
            columns.filter(
              (column) => column.formConfig?.groupId !== changedGroup?.id,
            ),
          );
        }
        if (type === 'edit') {
          setGroupColumns((columns) => {
            const target = columns.find(
              (column) => column.formConfig?.groupId === changedGroup?.id,
            );
            if (target) {
              const { groupName, isPureClientGroup, isEmptyGroup } =
                changedGroup;
              target.title = (
                <div className="col-title group-title">
                  <Tooltip title={groupName}>{groupName}</Tooltip>
                </div>
              );
              target.formConfig.isPureClientGroup = isPureClientGroup;
              target.formConfig.isEmptyGroup = isEmptyGroup;
            }
            return [...columns];
          });
        }
      });
      eventListener.on(
        eventNames.groupClientChange,
        function (changedGroups = []) {
          setGroupColumns((columns) => {
            changedGroups.forEach((changedGroupItem) => {
              const { isPureClientGroup, isEmptyGroup, groupId } =
                changedGroupItem;
              const target = columns.find(
                (column) => column.formConfig?.groupId === groupId,
              );
              target.formConfig.isPureClientGroup = isPureClientGroup;
              target.formConfig.isEmptyGroup = isEmptyGroup;
            });

            const allPureClientGroupIds = columns
              .filter((i) => i.formConfig.isPureClientGroup)
              .map((i) => i.formConfig.groupId);
            const allEmptyGroupIds = columns
              .filter((i) => i.formConfig.isEmptyGroup)
              .map((i) => i.formConfig.groupId);
            const form = cRef.current.getModuleForm();
            window.cForm = form;
            const formValues = form.getFieldsValue();
            const moduleRows = formValues?.customizeModule;
            if (Array.isArray(moduleRows)) {
              moduleRows.forEach((row, index) => {
                const internalValue = row.internalPermissions;
                const clientValue = row.clientPermissions;
                const groupValue = row.groupPermissions || [];
                const groupAllowedValues = groupValue
                  .filter(
                    (i) =>
                      !allPureClientGroupIds.includes(i.groupId) &&
                      !allEmptyGroupIds.includes(i.groupId),
                  )
                  .flatMap((i) => i.groupPermission);
                const internalValues = internalValue.map((i) => i.value);
                const allAvailablePermissionKey = [
                  ...groupAllowedValues,
                  ...internalValues,
                ];

                if (clientValue?.length) {
                  row.clientPermissions = clientValue.filter((i) =>
                    allAvailablePermissionKey.includes(i.value),
                  );
                }
                groupValue.forEach((i) => {
                  if (allEmptyGroupIds.includes(i.groupId) && index > 1) {
                    i.groupPermission = [];
                    return;
                  }
                  i.groupPermission = i.groupPermission.filter((key) =>
                    allAvailablePermissionKey.includes(key),
                  );
                });
              });
              form.setFieldsValue(formValues);
            }
            return [...columns];
          });
        },
      );
    }
  }, []);

  return (
    <>
      <div className="project-setting-module-wrapper manage-module-wrapper">
        {!createStep && (
          <p className="go-to-group-tip">
            If you want to add new group for the project, you can jump to{' '}
            <span onClick={() => changeTab('groups')}>Groups</span> tab.
          </p>
        )}
        <Module {...props} {...columnProps} />
        {/* <div>sddd</div> */}

        {renderAction && renderAction()}
      </div>
    </>
  );
}

export default forwardRef(ProjectModuleSetting);
